//Include Both Helper File with needed methods
import axios from "axios";
import {
  loginSuccess,
  logoutUserSuccess,
  apiError,
  reset_login_flag,
  setLoading,
  setPermissions,
  otpRequestSuccess,
  setOtpCooldown,
  setResendsRemaining,
  resetOtpLogin,
} from "./reducer";
import TokenManager from "../../../utils/tokenManager";
import secureApi from "../../../utils/secureApi";
import type { UserPermissions } from "../../../utils/roleBasedAccess";

const getResponsePayload = (response: any) => {
  const body = response?.data ?? response;
  if (body?.data?.authToken) return body.data;
  if (body?.authToken) return body;
  return body?.data ?? body;
};

const getAuthErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data ?? error?.data ?? error;
  return data?.error || data?.details?.[0] || data?.message || error?.message || fallback;
};

const getAuthErrorBody = (error: any) => error?.response?.data ?? error?.data ?? error;

const getAuthErrorStatus = (error: any): number | undefined => {
  const data = getAuthErrorBody(error);
  return error?.response?.status || data?.statusCode;
};

const getAuthErrorCode = (error: any): string => {
  const data = getAuthErrorBody(error);
  return data?.code || data?.errorCode || data?.details?.code || "";
};

const isOtpAttemptsBlockedError = (error: any): boolean => {
  const status = getAuthErrorStatus(error);
  const code = getAuthErrorCode(error);

  return (
    status === 423 ||
    status === 429 ||
    [
      "OTP_ATTEMPTS_EXCEEDED",
      "OTP_MAX_ATTEMPTS_EXCEEDED",
      "TOO_MANY_OTP_ATTEMPTS",
      "TOO_MANY_ATTEMPTS",
    ].includes(code)
  );
};

const isOtpValidationError = (error: any): boolean => {
  const status = getAuthErrorStatus(error);
  const code = getAuthErrorCode(error);

  return (
    status === 400 ||
    status === 410 ||
    [
      "INVALID_OTP",
      "INCORRECT_OTP",
      "OTP_INVALID",
      "OTP_INCORRECT",
      "OTP_EXPIRED",
      "EXPIRED_OTP",
    ].includes(code)
  );
};

const isOtpResendsExhaustedError = (error: any): boolean => {
  const code = getAuthErrorCode(error);

  return [
    "OTP_MAX_RESENDS_EXCEEDED",
    "MAX_OTP_RESENDS_EXCEEDED",
    "OTP_RESENDS_EXHAUSTED",
    "TOO_MANY_OTP_RESENDS",
  ].includes(code);
};

const getRetryAfterSeconds = (error: any): number => {
  const data = getAuthErrorBody(error);
  const retryAfterHeader = error?.response?.headers?.["retry-after"];

  return Number(
    data?.retryAfterSeconds ||
    data?.resendAvailableInSeconds ||
    data?.secondsUntilRetry ||
    data?.retryAfter ||
    retryAfterHeader ||
    0
  );
};

const completeLogin = async (responseData: any, dispatch: any, history: any) => {
  const authToken = responseData?.authToken || responseData?.accessToken || responseData?.token;

  if (!authToken) {
    throw new Error("Invalid response from server");
  }

  TokenManager.setToken(authToken, undefined, responseData.refreshToken);

  const userObj = {
    fullName: responseData.fullName || responseData.userName || responseData.username || '',
    username: responseData.username || responseData.userName || '',
    email: responseData.email || responseData.userEmail || '',
    profilePic: responseData.profilePic || '',
    id: responseData._id || '',
    subRole: responseData.subRole || null,
    role: responseData.subRole || null,
    permissions: responseData.permissions || {},
    authToken,
    clientId: responseData.clientId ?? responseData.client_id ?? undefined
  };

  TokenManager.setUserData(userObj);
  dispatch(loginSuccess(userObj));
  dispatch(loadUserPermissions() as any);
  dispatch(resetOtpLogin());
  history("/");
};

export const loadUserPermissions = () => async (dispatch: any) => {
  try {
    const user = TokenManager.getUserData();
    const userId =
      user?.id || user?._id || (user as any)?.user_id || (user as any)?.userId;
    if (!userId) {
      console.warn("loadUserPermissions: missing user id");
      return;
    }

    const response = await secureApi.get<any>(
      `/api/v1/auth/users/${userId}/permissions`
    );

    const permissionsData = response?.permissions;
    if (!permissionsData) {
      return;
    }

    const basePermissions: string[] = Array.isArray(permissionsData.permissions)
      ? permissionsData.permissions
      : [];

    const scopedRoles: any[] = Array.isArray(permissionsData.scoped_roles)
      ? permissionsData.scoped_roles
      : [];

    const scopedPermissionCodes: string[] = scopedRoles.flatMap((role) =>
      Array.isArray(role.permissions) ? role.permissions : []
    );

    const permissionCodes = Array.from(
      new Set([...basePermissions, ...scopedPermissionCodes])
    );

    const normalized: UserPermissions = {
      permissionCodes,
    };

    const academyIds: string[] = [];
    const tournamentIds: string[] = [];
    const coachingCenterIds: string[] = [];
    const clubIds: string[] = [];

    scopedRoles.forEach((role) => {
      const ids: string[] = Array.isArray(role.scope_ids)
        ? role.scope_ids
        : [];
      switch (role.scope_type) {
        case "academy":
          academyIds.push(...ids);
          break;
        case "tournament":
          tournamentIds.push(...ids);
          break;
        case "coaching_center":
          coachingCenterIds.push(...ids);
          break;
        case "clubs":
          clubIds.push(...ids);
          break;
        default:
          break;
      }
    });

    if (academyIds.length) {
      normalized.academyAccess = {
        hasAccess: true,
        academyIds: Array.from(new Set(academyIds)),
      };
    }
    if (tournamentIds.length) {
      normalized.tournamentAccess = {
        hasAccess: true,
        tournamentIds: Array.from(new Set(tournamentIds)),
      } as any;
    }
    if (coachingCenterIds.length) {
      normalized.coachingCenterAccess = {
        hasAccess: true,
        coachingCenterIds: Array.from(new Set(coachingCenterIds)),
      };
    }
    if (clubIds.length) {
      normalized.clubAccess = {
        hasAccess: true,
        clubIds: Array.from(new Set(clubIds)),
      };
    }

    dispatch(setPermissions(normalized));
  } catch (error) {
    console.error("Failed to load user permissions:", error);
  }
};

export const requestLoginOtp = (email: string) => async (dispatch: any) => {
  dispatch(setLoading(true));
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/v1/auth/admin/request-otp`, { email });
    const payload = getResponsePayload(response);
    dispatch(otpRequestSuccess({ email, data: payload }));
  } catch (error: any) {
    const status = getAuthErrorStatus(error);
    const message = getAuthErrorMessage(error, "Unable to request OTP. Please try again.");
    const retryAfterSeconds = getRetryAfterSeconds(error);

    if (status === 429 && retryAfterSeconds > 0) {
      dispatch(setOtpCooldown({ email, seconds: retryAfterSeconds }));
      return;
    }

    if (isOtpResendsExhaustedError(error)) {
      dispatch(setResendsRemaining(0));
    }

    dispatch(apiError({ data: message, field: "email" }));
  }
};

export const verifyLoginOtp = (payload: { email: string; otp: string }, history: any) => async (dispatch: any) => {
  dispatch(setLoading(true));
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/v1/auth/admin/verify-otp`, {
      email: payload.email,
      otp: payload.otp,
    });
    const responseData = getResponsePayload(response);
    await completeLogin(responseData, dispatch, history);
  } catch (error: any) {
    const status = getAuthErrorStatus(error);
    const rawMessage = getAuthErrorMessage(error, "Unable to verify OTP. Please try again.");
    const otpAttemptsBlocked = isOtpAttemptsBlockedError(error);
    const message =
      !otpAttemptsBlocked && isOtpValidationError(error)
        ? "OTP expired or incorrect OTP"
        : rawMessage;

    if (status === 404) {
      dispatch(resetOtpLogin());
    }

    dispatch(apiError({ data: message, field: "otp", otpAttemptsBlocked }));
  }
};

export const logoutUser = () => async (dispatch: any) => {
  const token = TokenManager.getToken();

  try {
    if (token) {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/auth/logout`,
        {},
        {
          headers: {
            Authorization: token,
          },
        }
      );
    }
  } catch (error) {
    console.error("Logout API failed:", error);
  } finally {
    TokenManager.logout();
    dispatch(logoutUserSuccess({}));
  }
};

export const socialLogin = (type: any, history: any) => async (dispatch: any) => {
  try {
    // Social login is not supported without Firebase
    // You can implement alternative social login providers here if needed
    throw new Error('Social login is not currently supported');
  } catch (error) {
    dispatch(apiError(error));
  }
};

export const resetLoginFlag = () => async (dispatch: any) => {
  try {
    dispatch(reset_login_flag());
  } catch (error) {
    dispatch(apiError(error));
  }
};
